import { Injectable, inject } from '@angular/core';
import {
  ActivatedRoute, ActivatedRouteSnapshot, CanActivate, GuardResult, RedirectCommand, Router, RouterStateSnapshot, UrlTree
} from '@angular/router';
import {firstValueFrom, from, Observable, of, Subject} from 'rxjs';
import {AuthService} from './auth.service';
import {catchError, filter, map, mergeMap, take, tap, timeout} from 'rxjs/operators';
import {Location} from '@angular/common';
import {Logger} from '../utils/logger';
import {FirebaseApi} from './firebase-api.service';
import {CommonUtil} from '../utils/common.util';
import {SubscriptionState} from '../model/subscription-state.enum';
import {DeveloperApiService} from './api/routes/developer-api.service';
import {UserSubscription} from '../model/user-subscription';
import firebase from 'firebase/compat/app';
import {SubscriptionPlan} from '../enums/subscription-plan.enum';
import {ProjectData} from '../model/project-data';
import {ProjectAccessLevel} from '../../canvas/enums/project-access-level.enum';
import {SettingsService} from '../../canvas/services/settings.service';
import {PlanAccessState} from './plan-access-state.service';
import {OrganizationService} from './organization.service';
import {EmbedAccess, EmbedDeveloper, embedRefusalReason, EmbedUnavailableReason, embedUnavailableUrl} from '../utils/embed-unavailable';
import {EmbedPassState} from './embed-pass-state.service';

/** localStorage key of the guest token an integrator's link left behind (NewProjectGuardService). */
const GUEST_TOKEN_KEY = 'accessToken';
/** How long an embed waits for its own session before asking for a pass without one. */
const EMBED_SESSION_WAIT_MS = 8000;

export enum ResultCodes {
  OK,
  ERROR_NOT_LOGGED_IN,
  IFRAME_UNAUTHORIZED,
}

@Injectable({
  providedIn: 'root'
})
export class CanvasAuthGuardService implements CanActivate {
  private auth = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private developerApi = inject(DeveloperApiService);
  private firebaseApi = inject(FirebaseApi);
  private settingsService = inject(SettingsService);
  private planAccess = inject(PlanAccessState);
  private organization = inject(OrganizationService);
  private embedPass = inject(EmbedPassState);

  public loader$ = new Subject<boolean>();

  getConfiguredUrl(route: ActivatedRouteSnapshot): string {
    return '/' + route.pathFromRoot
      .filter(v => v.routeConfig)
      .map(v => v.routeConfig!.path)
      .join('/');
  }

  getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(v => v.url.map(segment => segment.toString()).join('/'))
      .join('/');
  }

  /** The project's creator or one of its members, rather than a visitor. */
  private static isOnProject(project: ProjectData, uid: string): boolean {
    return !!project && !!uid
      && (project.creator_id === uid || (project.members || []).includes(uid));
  }

  private static isPlanActive(subscription: UserSubscription): boolean {
    // No subscription read back used to throw here, failing the navigation.
    if (!subscription) {
      return false;
    }
    let expiresOn = subscription.expires_on;
    if (!expiresOn || Number.isNaN(expiresOn) || isNaN(expiresOn)) {
      expiresOn = subscription.period_end;
    }
    return expiresOn > new Date().getTime()
      && subscription.state !== SubscriptionState.PAST_DUE
      && subscription.state !== SubscriptionState.EXPIRED
      && subscription.state !== SubscriptionState.UNPAID
      && subscription.state !== SubscriptionState.INCOMPLETE;
  }

  /**
   * Into the canvas only on a plan that is still running; otherwise to
   * Subscription. Returned as a redirect rather than navigate() beside a
   * `false`: the "not signed in" handling below then sent the account to
   * /login as well, which bounced it on to the dashboard instead.
   */
  private requireActivePlan(uid: string, projectId?: string): Observable<boolean | UrlTree> {
    return from(this.firebaseApi.getSubscription(uid)).pipe(
      take(1),
      map((subscription: UserSubscription) => {
        if (CanvasAuthGuardService.isPlanActive(subscription)) {
          // Renewed since a shared visit left it read-only.
          this.planAccess.setLapsed(projectId, false);
          return true;
        }
        Logger.error('Access denied - Subscription Expired');
        return this.router.createUrlTree(['/dashboard/subscription']);
      }));
  }

  /**
   * A shared project's own people on a plan that has ended: in, but read-only
   * (PlanAccessState). They are not sent to Subscription, since anyone with
   * the project's link may look at it.
   */
  private viewOnLapsedPlan(uid: string, projectId: string): Observable<boolean> {
    return from(this.firebaseApi.getSubscription(uid)).pipe(
      take(1),
      map((subscription: UserSubscription) => {
        this.planAccess.setLapsed(projectId, !CanvasAuthGuardService.isPlanActive(subscription));
        return true;
      }),
      catchError(() => {
        this.planAccess.setLapsed(projectId, true);
        return [true];
      }));
  }

  /**
   * Whether the account works on the project, so that its plan decides
   * whether it may change it: the creator or a member, or an Admin or Manager
   * of the creator's organisation (OrganizationService.isStaffFor), whom the
   * canvas lets edit every project of the organisation. Staff were left out
   * of the lapsed-plan check, and on a project shared view-only, public or
   * Open Guest they kept full editing after their plan had ended.
   */
  private async worksOnProject(project: ProjectData, user: firebase.User): Promise<boolean> {
    if (!project || !user || !user.emailVerified || user.isAnonymous) {
      return false;
    }
    if (CanvasAuthGuardService.isOnProject(project, user.uid)) {
      return true;
    }
    // A member's projects need the organisation's member list.
    await this.organization.ready().catch(() => null);
    return this.organization.isStaffFor(project.creator_id);
  }

  /**
   * The guest token an integrator's link left in this browser, for this
   * project only, and only for a visitor without an account of its own.
   *
   * It used to be sent for every project opened in the browser, ahead of the
   * project's own sharing: other projects were refused, and once the token
   * expired (within the day) so was the guest's own project -- off to the
   * sign-in page, which a guest cannot use -- until site data was cleared. A
   * signed-in account opening its own projects was refused the same way.
   */
  private storedGuestToken(projectId: string, user: firebase.User): string | null {
    if (!projectId || (user && !user.isAnonymous)) {
      return null;
    }
    const stored = this.settingsService.getLocalValue(GUEST_TOKEN_KEY);
    if (!stored) {
      return null;
    }
    if (typeof stored === 'object') {
      return stored.projectId === projectId && typeof stored.token === 'string' && stored.token
        ? stored.token
        : null;
    }
    // A bare token from an older version: it does not say which project it
    // opened, and the project's own link opens an Open Guest project anyway.
    this.forgetGuestToken();
    return null;
  }

  private forgetGuestToken() {
    this.settingsService.setLocalValue(GUEST_TOKEN_KEY, '');
  }

  /**
   * The project's own link: open to anyone when it is shared view-only,
   * public or Open Guest, otherwise to signed-in accounts on a running plan
   * (the canvas and the rules then decide what they may see).
   */
  private linkAccess(user: firebase.User, projectId: string): Observable<boolean | UrlTree> {
    // The rules answer a private project someone may not read with
    // permission-denied rather than an empty document. That is the
    // same outcome as "not shared" here: signed-in accounts go on to
    // the canvas, which reports access denied; others are refused.
    return from(this.firebaseApi.getProjectRef(projectId).ref.get().catch(() => null)).pipe(
      take(1),
      mergeMap(snapshot => {
        const proj = snapshot ? snapshot.data() as ProjectData : null;
        // Precedence: && binds tighter than ||, so this used to read
        // as (proj && VIEW_ONLY) || (proj.access_level === PUBLIC),
        // and a project id that does not exist threw on the second
        // half instead of falling through to the not-found path.
        // Open Guest belongs here too. Its whole point is a visitor with no
        // account measuring, and until now the only way in carried a custom
        // token -- which expires within the day, so a guest link on a
        // website died by the afternoon. With an anonymous session the
        // project's own link is enough, and a guest can come back to it.
        const shared = !!proj && (proj.access_level === ProjectAccessLevel.VIEW_ONLY
          || proj.access_level === ProjectAccessLevel.PUBLIC
          || proj.access_level === ProjectAccessLevel.OPEN_GUEST);
        if (shared) {
          if (user) {
            // The project's own people work on it under their plan,
            // however it is shared. A lapsed owner was sent to
            // Subscription from a private project but kept full
            // editing on any project shared view-only, public or Open
            // Guest; now they may only look. Visitors are not affected.
            return from(this.worksOnProject(proj, user)).pipe(
              take(1),
              mergeMap(works => works ? this.viewOnLapsedPlan(user.uid, proj.project_id) : of(true)));
          }
          // A visitor with no account still needs an identity the
          // server can verify, because leaving a comment on a
          // view-only project is something they are allowed to do.
          // The same anonymous sign-in already happens for Open
          // Guest projects and inside the iframe; a plain share link
          // was the one way in that skipped it, so the comment POST
          // had no token to send and used to carry a fixed password
          // instead.
          //
          // Viewing must not depend on it: if the sign-in fails the
          // project still opens, and only commenting is lost.
          return from(this.firebaseApi.auth().signInAnonymously()
            .then(() => true)
            .catch(() => true));
        } else {
          if (user && user.emailVerified) {
            // proj is missing for a deleted project or a refused read: the canvas
            // then says not found / no access, as before.
            return this.requireActivePlan(user.uid, proj?.project_id);
          }else{
            return Promise.resolve(false);
          }
        }
      })
    );
  }

  /**
   * A private project its owner lets the key's developer show (Authorized
   * developers) is read with a pass the server gives this session when it
   * answers the check. A first visit can ask before the iframe has a session
   * of its own (AuthService signs a visitor in anonymously as the app
   * starts): then the server answers `authorized` without a pass, and this
   * waits for the session and asks once more. Every other answer is passed
   * on as it came, so nothing changes for other embeds.
   */
  private async withEmbedPass(access: EmbedAccess, projectId: string,
                              devToken: string | null, devKey: string | null): Promise<EmbedAccess> {
    if (!access.granted || !access.authorized || !projectId || !CommonUtil.inIframe()) {
      return access;
    }
    const ask = () => this.developerApi.checkApiKeyProjectAccess(projectId, devToken, devKey, false);
    let result = access;
    if (!result.pass) {
      const session = await firstValueFrom(this.auth.currentUserObservable.pipe(
        filter(u => !!u), take(1), timeout({first: EMBED_SESSION_WAIT_MS}), catchError(() => of(null))));
      if (session) {
        // The ID token that goes with the check is this session's.
        this.firebaseApi.currentUser = session;
        result = await ask();
      }
    }
    if (result.granted && result.authorized && result.pass) {
      this.embedPass.hold(projectId, () => ask().then(again => !!(again.granted && again.pass)));
    }
    return result;
  }

  /** The project as this browser may read it; `denied` when the rules refuse it. */
  private readProject(projectId: string): Promise<{project: ProjectData | null, denied: boolean}> {
    return this.firebaseApi.getProject(projectId).then(
      project => ({project: project || null, denied: false}),
      error => ({project: null, denied: !!error && error.code === 'permission-denied'}));
  }

  /**
   * Why the server refused an embed. Its "not allowed" covers both a website
   * the key does not allow and another account's private project; anyone may
   * read a shared project, so reading it tells the two apart. A private
   * project's refusal names the embed's developer, and says so itself.
   */
  private async embedRefusal(projectId: string, access: EmbedAccess): Promise<RedirectCommand> {
    if (access.developer && projectId) {
      return this.embedUnavailable('private', projectId, access.developer);
    }
    let reason = embedRefusalReason(access.errorCode);
    if (reason === 'site' && projectId) {
      const read = await this.readProject(projectId);
      reason = read.denied ? 'private' : read.project ? 'site' : 'unavailable';
    }
    return this.embedUnavailable(reason, projectId);
  }

  /**
   * The page that tells an embed's visitor why the project is not shown,
   * without changing the address: reloading then tries the embed again
   * instead of reloading the message. The developer goes in the navigation's
   * state, not the address, so no link can put another name on the page.
   */
  private embedUnavailable(reason: EmbedUnavailableReason, projectId: string = '', developer?: EmbedDeveloper): RedirectCommand {
    return new RedirectCommand(this.router.parseUrl(embedUnavailableUrl(reason, projectId)),
      {skipLocationChange: true, ...(developer ? {state: {embedDeveloper: developer}} : {})});
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<GuardResult> | boolean {
    this.loader$.next(true);
    Logger.log('canActivate CanvasAuthGuardService ', next)

    const demoModeStr = next.queryParams.demoMode;
    let projectId = next.queryParams.pid;
    if (!projectId) {
      const pattern = /\/p\/([^/]+)/;
      const match = state.url.split('?')[0].match(pattern);
      projectId = match ? match[1] : null;
    }
    let demoMode = false;
    if (demoModeStr) {
      demoMode = Boolean(demoModeStr);
    }
    const params = next.queryParams;
    const devKey = params.key;
    const urlToken = params.token;
    const displayName = next.queryParams.displayName ? next.queryParams.displayName : 'Guest';

    return this.auth.currentUserObservable.pipe(
      take(1),
      mergeMap((user: firebase.User) => {
        if (user) {
          this.firebaseApi.currentUser = user;
        }
        if ((!user || !user.emailVerified) && demoMode) {
          return Promise.resolve(demoMode);
        }else{
          if (demoMode) {
            return Promise.resolve(true);
          }
          const storedToken = devKey || urlToken ? null : this.storedGuestToken(projectId, user);
          const devToken = urlToken || storedToken;
          if(devKey || devToken) {
            Logger.log('iFRame API ', projectId);
            return from(this.developerApi.checkApiKeyProjectAccess(projectId, devToken, devKey, false)).pipe(
              take(1),
              mergeMap((access: EmbedAccess) => from(this.withEmbedPass(access, projectId, devToken, devKey))),
              mergeMap((access: EmbedAccess) => {
                const canAccess = access.granted;
                Logger.log('Can Access', canAccess, 'User', user, 'ProjectId', projectId);
                if (!canAccess) {
                  // An expired or refused stored token is dropped, and the
                  // project's own sharing decides instead of a refusal.
                  if (storedToken) {
                    this.forgetGuestToken();
                    return this.linkAccess(user, projectId);
                  }
                  // So does a key or token in the address outside an embed,
                  // such as a guest's history entry from yesterday: the
                  // allowed-domain rules are about embedding, and the
                  // project's own link grants nothing more than it would
                  // without them. Inside an embed a refused key stays refused.
                  if (!CommonUtil.inIframe()) {
                    return this.linkAccess(user, projectId);
                  }
                  // Inside an embed a refused key stays refused, and the
                  // visitor is told why rather than shown a bare 403.
                  return from(this.embedRefusal(projectId, access));
                }
                if (!projectId || (user && !CommonUtil.inIframe())) {
                  return Promise.resolve(true);
                }
                Logger.log('Can Access2', canAccess, 'User', user, 'ProjectId', projectId);
                // Granted is not yet readable. The key's own private projects
                // pass the server's check (its account may edit them), but the
                // iframe is not signed in as that account, so the rules refuse
                // the read -- and since 8.0.0 the map then said "project not
                // found". The server has just found the project, so a refused
                // read means it is private: the embed says so.
                return from(this.readProject(projectId)).pipe(
                  take(1),
                  mergeMap(({project: proj, denied}) => {
                    Logger.log('Can Access 3', canAccess, 'User', user, 'ProjectId', projectId);
                    if (denied && CommonUtil.inIframe()) {
                      return of(this.embedUnavailable('private', projectId));
                    }
                    // A signed-in embed visitor needs nothing more.
                    if (user) {
                      return of(true);
                    }
                    if(proj && proj.access_level === ProjectAccessLevel.OPEN_GUEST) {
                      Logger.log('Can Access 4', canAccess, 'User', user, 'ProjectId', projectId);
                      if(this.auth.isLoggedIn()) {
                        return Promise.resolve(true);
                      } else {
                        return from(this.firebaseApi.auth().signInAnonymously()).pipe(
                          take(1),
                          mergeMap(cred => {
                            if(displayName){
                              cred.user.updateProfile({
                                displayName: displayName
                              })
                              this.settingsService.setLocalValue('user_full_name', displayName)
                            }
                            Logger.log('Can Access 5', canAccess, 'User', cred, 'ProjectId', projectId);
                            if(cred && cred.user) {
                              Logger.log('Can Access 6', canAccess, 'User', cred, 'ProjectId', projectId);
                              return Promise.resolve(true);
                            }
                            return Promise.resolve(false);
                          })
                        );
                      }
                    } else {
                      // A private project outside an embed: the canvas reports
                      // access denied, as before.
                      return Promise.resolve(true);
                    }
                  })
                );
              }),
            );
          }
          return this.linkAccess(user, projectId);
        }
        // if (CommonUtil.inIframe()) {
        //   Logger.log('iFRame API ', projectId);
        //   const devKey = next.queryParams.key;
        //   const devToken = next.queryParams.token;
        //   if (demoMode) {
        //     return Promise.resolve(true);
        //   }
        //   return this.developerApi.checkApiKeyProjectAccess(projectId, devToken, devKey);
        // } else if (user && user.emailVerified) {
        //   return from(this.firebaseApi.getSubscription(user.uid)).pipe(
        //     take(1),
        //     map((subscription: UserSubscription)  => {
        //       return subscription
        //         && subscription.expires_on > new Date().getTime()
        //         && subscription.state !== SubscriptionState.PAST_DUE
        //         && subscription.state !== SubscriptionState.EXPIRED
        //         && subscription.state !== SubscriptionState.UNPAID
        //         && subscription.state !== SubscriptionState.INCOMPLETE;
        //     }),
        //     tap(canAccess => {
        //       if (!canAccess) {
        //         Logger.error('Access denied - Subscription Expired');
        //         this.router.navigate(['/dashboard/subscription']);
        //       }
        //     }));
        // } else if ((!user || !user.emailVerified) && demoMode) {
        //   return Promise.resolve(demoMode);
        // } else {
        //   if (!projectId) {
        //     return Promise.resolve(false);
        //   }
        //   return from(this.firebaseApi.getProjectData(projectId)).pipe(
        //     take(1),
        //     catchError(val => {
        //       return Promise.resolve(false);
        //     }),
        //     map(project => {
        //       return !!project;
        //     }),
        //     tap(canAccess => {
        //     }));
        // }
      }),
      tap(canAccess => {
        Logger.log('canAccess: ', canAccess, this.location.path(), `Is in iFrame: ${CommonUtil.inIframe()}`);
        if (!canAccess && !CommonUtil.inIframe()) {
          Logger.error('Access denied - Not Logged in');
          this.router.navigate(['/login'], {queryParams: {rdt: state.url}});
        } else if (!canAccess) {
          Logger.error('Access denied');
          // In an embed: refused without a reason from the server (a stored
          // guest token that no longer works, on a project that is not shared).
          this.router.navigateByUrl(embedUnavailableUrl('unavailable'), {skipLocationChange: true});
        }
        // this.loader$.next(false);
      })
    );
  }
}
